import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { api, type MenuImportRow } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import { euro } from '../../lib/format';

const HEADERS = ['Catégorie', 'Imprimante', 'Plat', 'Prix (€)', 'Description', 'Suppléments', 'Sauces', 'Allergènes', 'Badges', 'Plat (EN)', 'Description (EN)', 'Emoji'];
const EXAMPLE = [
  { 'Catégorie': 'Entrées', 'Imprimante': 'Thaï', 'Plat': 'Nems Poulet (x4)', 'Prix (€)': '6,50', 'Description': 'Sauce aigre-douce', 'Suppléments': '', 'Sauces': 'Aigre-douce ; Piquante', 'Allergènes': 'Gluten, Soja', 'Badges': '', 'Plat (EN)': 'Chicken Nems (x4)', 'Description (EN)': 'Sweet & sour sauce', 'Emoji': '🥟' },
  { 'Catégorie': 'Currys Thaï', 'Imprimante': 'Thaï', 'Plat': 'Curry Vert Poulet', 'Prix (€)': '13,90', 'Description': 'Lait de coco, basilic', 'Suppléments': 'Extra poulet:2,50 ; Extra fromage:1,50', 'Sauces': 'Doux ; Moyen ; Fort', 'Allergènes': 'Crustacés', 'Badges': 'Signature, Épicé', 'Plat (EN)': 'Green Curry Chicken', 'Description (EN)': '', 'Emoji': '🍛' },
  { 'Catégorie': 'Boissons', 'Imprimante': 'Boissons & Desserts', 'Plat': 'Mango Lassi', 'Prix (€)': '4,50', 'Description': 'Yaourt mangue', 'Suppléments': '', 'Sauces': '', 'Allergènes': 'Lait', 'Badges': '', 'Plat (EN)': 'Mango Lassi', 'Description (EN)': '', 'Emoji': '🥭' },
];

// enlève accents/espaces/casse : "Prix (€)" → "prix", "Catégorie" → "categorie"
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '');
const parsePrice = (v: unknown): number => {
  if (typeof v === 'number') return Math.round(v * 100);
  const s = String(v ?? '').replace(/[^0-9,.-]/g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? NaN : Math.round(n * 100);
};
const splitList = (v: unknown): string[] => String(v ?? '').split(/[;,]/).map((x) => x.trim()).filter(Boolean);
// "Extra fromage:1,50 ; Bacon:2 ; Curry" → [{name,priceCents}] (prix optionnel, séparateur « ; »)
const parseOptions = (v: unknown): { name: string; priceCents: number }[] =>
  String(v ?? '').split(/[;\n]/).map((part) => {
    const idx = part.indexOf(':');
    const name = (idx >= 0 ? part.slice(0, idx) : part).trim();
    if (!name) return null;
    const priceRaw = idx >= 0 ? part.slice(idx + 1) : '';
    const cents = priceRaw.trim() ? parsePrice(priceRaw) : 0;
    return { name, priceCents: isNaN(cents) ? 0 : cents };
  }).filter(Boolean) as { name: string; priceCents: number }[];

function mapRow(raw: Record<string, unknown>): MenuImportRow {
  const get = (...keys: string[]) => {
    for (const k of Object.keys(raw)) if (keys.includes(norm(k))) return raw[k];
    return undefined;
  };
  return {
    category: String(get('categorie', 'category') ?? '').trim(),
    name: String(get('plat', 'nom', 'name') ?? '').trim(),
    priceCents: parsePrice(get('prix', 'price')),
    nameEn: String(get('platen', 'nomen', 'nameen') ?? '').trim() || undefined,
    description: String(get('description') ?? '').trim() || undefined,
    descriptionEn: String(get('descriptionen') ?? '').trim() || undefined,
    allergens: splitList(get('allergenes', 'allergens')),
    badges: splitList(get('badges', 'badge')),
    emoji: String(get('emoji') ?? '').trim() || undefined,
    station: String(get('imprimante', 'poste', 'printer', 'station') ?? '').trim() || undefined,
    supplements: parseOptions(get('supplements', 'supplement', 'extras', 'extra')),
    sauces: parseOptions(get('sauces', 'sauce')),
  };
}
const rowError = (r: MenuImportRow): string | null =>
  !r.category ? 'Catégorie manquante'
  : !r.name ? 'Plat manquant'
  : isNaN(r.priceCents) ? 'Prix illisible'
  : r.priceCents < 0 ? 'Prix négatif'
  : null;

export function MenuImport({ onBack, onDone }: { onBack: () => void; onDone?: () => void }) {
  const push = useToasts((s) => s.push);
  const [rows, setRows] = useState<MenuImportRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(EXAMPLE, { header: HEADERS });
    ws['!cols'] = HEADERS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Carte');
    XLSX.writeFile(wb, 'modele-carte-taj-fusion.xlsx');
  };

  const onFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
      const mapped = json.map(mapRow).filter((r) => r.category || r.name); // ignore lignes vides
      if (!mapped.length) return push('Fichier vide ou colonnes non reconnues.', 'warn');
      setRows(mapped);
      setFileName(file.name);
    } catch (e: any) {
      push('Lecture impossible : ' + (e?.message ?? 'fichier invalide'), 'warn');
    }
  };

  const errors = rows.map(rowError);
  const validCount = errors.filter((e) => !e).length;
  const errCount = errors.filter(Boolean).length;
  const cats = new Set(rows.filter((_, i) => !errors[i]).map((r) => r.category)).size;

  const submit = async () => {
    const valid = rows.filter((_, i) => !errors[i]);
    if (!valid.length) return push('Aucune ligne valide à importer.', 'warn');
    if (replace && !confirm(`Remplacer toute la carte ?\nLes plats absents du fichier seront masqués. (${valid.length} plats importés)`)) return;
    setBusy(true);
    push('⏳ Synchronisation de la carte…');
    try {
      const res = await api.importMenu(valid, replace);
      push(`✅ Carte synchronisée : ${res.created} ajoutés, ${res.updated} mis à jour${replace ? `, ${res.hidden} masqués` : ''} · ${res.categories} catégories`);
      setRows([]); setFileName('');
      onDone?.();
    } catch (e: any) { push('❌ ' + e.message, 'warn'); }
    finally { setBusy(false); }
  };

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>📥 Importer la carte (Excel)</div>
      </div>

      <div className="cc" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>1. Téléchargez le modèle, remplissez-le dans Excel</div>
        <div className="staff-subtitle" style={{ margin: '0 0 10px', lineHeight: 1.5 }}>
          Obligatoires : <b>Catégorie</b>, <b>Plat</b>, <b>Prix (€)</b>. Optionnels : <b>Imprimante</b> (poste cuisine
          de la catégorie, ex. « Thaï »), <b>Suppléments</b> et <b>Sauces</b> (format <code>Nom:prix ; Nom:prix</code>,
          ex. <code>Extra fromage:1,50 ; Bacon:2</code> — prix facultatif), Description, Allergènes (séparés par virgules),
          Badges (Végé, Épicé…), Plat (EN), Description (EN), Emoji.
        </div>
        <button className="btn-gold" onClick={downloadTemplate}>⬇️ Télécharger le modèle Excel</button>
      </div>

      <div className="cc" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>2. Envoyez votre fichier rempli</div>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
        />
        <button className="cf-reprint" style={{ margin: 0 }} onClick={() => fileRef.current?.click()}>📂 Choisir un fichier Excel</button>
        {fileName && <span style={{ marginLeft: 10, color: 'var(--ink-dim)', fontWeight: 700 }}>📄 {fileName}</span>}
      </div>

      {rows.length > 0 && (
        <div className="cc">
          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            3. Vérifiez puis validez — {validCount} plat(s) valides{errCount > 0 && <span style={{ color: 'var(--cuisine)' }}> · {errCount} erreur(s)</span>} · {cats} catégorie(s)
          </div>
          <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--paper)' }}>
                  <th style={th}>Catégorie</th><th style={th}>Plat</th><th style={{ ...th, textAlign: 'right' }}>Prix</th><th style={th}>Infos</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ background: errors[i] ? 'rgba(192,57,43,.08)' : undefined }}>
                    <td style={td}>{r.category || <em style={{ color: 'var(--cuisine)' }}>—</em>}</td>
                    <td style={td}>{r.name || <em style={{ color: 'var(--cuisine)' }}>—</em>}{r.emoji ? ' ' + r.emoji : ''}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{isNaN(r.priceCents) ? '—' : euro(r.priceCents)}</td>
                    <td style={{ ...td, color: errors[i] ? 'var(--cuisine)' : 'var(--ink-faint)', fontWeight: errors[i] ? 800 : 600 }}>
                      {errors[i] ?? [
                        r.station ? '🖨️ ' + r.station : '',
                        r.supplements?.length ? '➕ ' + r.supplements.length + ' suppl.' : '',
                        r.sauces?.length ? '🥫 ' + r.sauces.length + ' sauce(s)' : '',
                        r.badges?.length ? '🏷️ ' + r.badges.join(', ') : '',
                        r.allergens?.length ? '⚠️ ' + r.allergens.join(', ') : '',
                      ].filter(Boolean).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 2px', cursor: 'pointer' }}>
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
            <span><b>Remplacer toute la carte</b> — masquer les plats/catégories absents du fichier (sinon on ajoute/met à jour sans rien enlever)</span>
          </label>

          <button className="btn-gold" style={{ width: '100%' }} disabled={busy || validCount === 0} onClick={submit}>
            ✓ Valider et synchroniser ({validCount} plats)
          </button>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--line)', fontWeight: 800, color: 'var(--ink-dim)' };
const td: React.CSSProperties = { padding: '7px 10px', borderBottom: '1px solid var(--line)' };
